'use client';
import { bgPrimary } from '@osmosis/tailwind-ui/styles/background';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';

import React from 'react';
import { useRef } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { usePagesContext } from '../../../context/PagesProvider';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import { useUserContext } from '../../../context/UserContext';
import InputLabel from '../../../components/auth/InputLabel';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputValidation from '../../../components/InputValidation';
import InputValidationMessage from '../../../components/InputValidationMessage';

type Props = {
  handleClose: () => void;
  showNonPrimeModal: () => void;
};

/**
 * Renders modal box to add new playlist
 * handleClose - the function that is used to hide this modal window.
 */
const NewPlaylistModalBox = (props: Props) => {
  const { handleClose, showNonPrimeModal } = props;
  const intl = useIntl();
  const { createPlaylist } = usePlaylistContext();
  const { push } = usePagesContext();
  const { hasPrime } = useUserContext();

  const [title, setTitle] = React.useState<string>('');
  const [isPlaylistErrorActive, setPlaylistErrorActive] = React.useState(false);
  const [sameNameError, setSameNameError] = React.useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const validationSchema = yup
    .object({
      title: yup.string().required(
        intl.formatMessage({
          defaultMessage: 'Playlist title is required.',
          id: 'playlist.new-playlist-modal-box.titleRequired',
        }),
      ),
    })
    .required('Playlist title is required.');

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<{ title: string }>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const playlistTitleErrorMsg = errors?.title?.message?.toString();

  const onSubmit: SubmitHandler<{ title: string }> = async ({
    title,
  }: {
    title: string;
  }) => {
    if (!hasPrime) {
      handleClose();
      showNonPrimeModal();
    } else {
      setTitle(title);
      const data = await createPlaylist({
        title,
        options: {
          source: 'new-playlist-modal-box',
        },
      });
      if (data?.success) {
        if (!data?.alreadyExists) {
          handleClose();
          await push(`/playlist/${data.url}`);
        } else {
          setSameNameError(true);
          inputRef.current?.focus();
        }
      } else {
        if (!isPlaylistErrorActive) setPlaylistErrorActive(true);
      }
    }
  };
  const handleInputRef = (e: HTMLInputElement | null) => {
    register('title').ref(e);
    inputRef.current = e;
  };

  return (
    <Box className={`min-w-[unset] md:min-w-[400px] lg:min-w-[600px] p-4`}>
      <form
        name="playlist.new-playlist-modal-box.form"
        data-testid="playlist.new-playlist-modal-box.form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <InputLabel
          content={
            <FormattedMessage
              id="playlist.new-playlist-modal-box.title"
              defaultMessage="Title"
            />
          }
          htmlFor="title"
          data-testid="playlist.new-playlist-modal-box.title"
          hasError={!!playlistTitleErrorMsg || sameNameError}
        />

        <label htmlFor="newPlaylistModalBox-input">
          <InputValidation
            showErrorMessage={false}
            error={!!playlistTitleErrorMsg || sameNameError}
            variant="filled"
            inputProps={{
              ...register('title', {
                onChange: () => {
                  setPlaylistErrorActive(false);
                  setSameNameError(false);
                },
                value: title ? title : '',
              }),
              placeholder: intl.formatMessage({
                defaultMessage: 'New playlist title',
                id: 'playlist.new-playlist-modal-box.placeholder',
              }),
              ref: handleInputRef,
              ['data-testid']: 'newPlaylistModalBox-input',
              ['aria-invalid']:
                playlistTitleErrorMsg || sameNameError ? 'true' : 'false',
              ['aria-describedby']: playlistTitleErrorMsg
                ? 'invalid-playlist-title-msg'
                : sameNameError
                  ? 'same-name-error-msg'
                  : undefined,
            }}
            containerProps={{
              cn: 'mb-3',
            }}
          />
        </label>
        <Flex className={`justify-center mt-8`}>
          <Button
            type="button"
            data-testid="new-playlist-modal-cancel-btn"
            onClick={handleClose}
            variant="outline"
            className={`mr-2 min-w-[100px]`}
          >
            <FormattedMessage
              defaultMessage="Cancel"
              id="playlist.new-playlist-modal-box.cancel"
            />
          </Button>
          <Button
            data-testid="newPlaylistModalBox-createBtn"
            className={`ml-2 ${bgPrimary} min-w-[100px]`}
          >
            <FormattedMessage
              defaultMessage="Create"
              id="playlist.new-playlist-modal-box.create"
            />
          </Button>
        </Flex>
        <InputValidationMessage
          error={!!playlistTitleErrorMsg}
          errorMessage={playlistTitleErrorMsg}
          messageProps={{
            'data-testid': 'invalid-playlist-title-msg',
            id: 'invalid-playlist-title-msg',
          }}
        />

        <InputValidationMessage
          error={!!isPlaylistErrorActive}
          errorMessage={
            <FormattedMessage
              defaultMessage="Error creating playlist"
              id="playlist.new-playlist-modal-box.errorCreating"
            />
          }
          messageProps={{
            'data-testid': 'error-creating-playlist-msg',
            id: 'error-creating-playlist-msg',
          }}
        />

        <InputValidationMessage
          error={!!sameNameError}
          errorMessage={
            <FormattedMessage
              defaultMessage="A playlist with this name already exists"
              id="playlist.new-playlist-modal-box.sameNameError"
            />
          }
          messageProps={{
            'data-testid': 'same-name-error-msg',
            id: 'same-name-error-msg',
          }}
        />
      </form>
    </Box>
  );
};

export default NewPlaylistModalBox;
